import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Film } from 'lucide-react';
import { listSessions, getSessionFacets } from '../../services/api';
import { getBestLapTime, isBestLap } from '../../utils/lapStats';
import { SF_MISMATCH_WARN_M } from '../../utils/sfAlignment';
import { convertWeatherUnits } from '../../utils/unitConvert';
import useDateRangePresets, { DAY_MS } from '../common/useDateRangePresets';
import {
  SessionPrimaryFilters,
  SessionAdvancedFilters,
  SessionOwnershipTabs,
} from '../common/SessionBrowserControls';
import {
  SESSION_TABLE_COLS,
  comparisonGapSeconds,
  formatComparisonGroupDate,
  formatSessionDateTimeCompact,
  formatSessionDuration,
  formatSessionTime12,
  groupComparisonSessions,
  isComparisonSessionSelectable,
  isComparisonSessionVisible,
  nextSessionSort,
  readCompareSessionFilters,
  sessionHasVideo,
  sessionTrackDisplayName,
  sessionWeatherIcon,
  shortSessionYear,
  writeCompareSessionFilters,
} from '../common/sessionBrowser';
import styles from './LapSelector.module.css';
import { LapHealthDot, findingsForLap } from '../CarHealth/HealthChip';
import tableStyles from '../Upload/UploadPanel.module.css';

// Sentinel index for the synthetic Theoretical Best lap. Mirrors
// TB_LAP_INDEX in hooks/useSession.js.
export const TB_LAP_INDEX = -1;
export const TB_COLOR     = '#FFD700';

// Palette for primary driver lap colors
export const LAP_COLORS = [
  '#e8411a', '#4da6ff', '#44cc88', '#f5c842',
  '#cc44cc', '#ff8844', '#44cccc', '#ff4488',
  '#88ff44',
];

// Palette for the first comparison driver (cool blue/cyan family)
export const COMPARE_COLORS = [
  '#00cfff', '#00ffcc', '#4499ff', '#aae4ff',
  '#0099dd', '#55ddff', '#22bbcc', '#88ccff',
  '#00ddaa',
];

// Palette for the second comparison driver (green family)
export const COMPARE_COLORS_2 = [
  '#44cc88', '#a6ff4d', '#22cc66', '#cdf5a6',
  '#2faa55', '#88dd55', '#66cc22', '#bbff88',
  '#33dd99',
];

// Palette for the third comparison driver (purple/magenta family)
export const COMPARE_COLORS_3 = [
  '#c084fc', '#ff7ad9', '#a855f7', '#e9c5ff',
  '#9333ea', '#d86bff', '#bb55cc', '#f0aaff',
  '#cc66dd',
];

// Comparison palettes indexed by comparison-driver slot (0-based). The Nth
// comparison driver (Driver N+2 in the UI) uses COMPARE_PALETTES[N].
export const COMPARE_PALETTES = [COMPARE_COLORS, COMPARE_COLORS_2, COMPARE_COLORS_3];

export function getLapColor(lapIndex, selectedLaps, palette, customColors) {
  if (lapIndex === TB_LAP_INDEX) return TB_COLOR;
  if (customColors && customColors[lapIndex]) return customColors[lapIndex];
  const idx = selectedLaps.indexOf(lapIndex);
  if (idx < 0) return null;
  // Skip TB sentinel when computing palette offset so real laps keep stable colors
  const realIdx = selectedLaps.slice(0, idx + 1).filter(i => i !== TB_LAP_INDEX).length - 1;
  return palette[(realIdx >= 0 ? realIdx : idx) % palette.length];
}

// De-duped: the four palettes share a few hex values (e.g. '#44cc88' appears in
// both LAP_COLORS and COMPARE_COLORS_2), which would otherwise produce duplicate
// React keys in the swatch grid below.
const SWATCH_PRESETS = [...new Set([
  ...LAP_COLORS,
  ...COMPARE_COLORS,
  ...COMPARE_COLORS_2,
  ...COMPARE_COLORS_3,
  '#ffffff', '#ff0000', '#00ff00', '#0000ff',
  '#ffff00', '#ff00ff', '#00ffff', '#888888',
])];

function ColorPickerPopover({ currentColor, anchorRect, onSelect, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Position to the right of the anchor, with viewport clamping
  const style = { position: 'fixed' };
  if (anchorRect) {
    const popoverW = 170;
    const left = anchorRect.right + 6;
    const top  = anchorRect.top - 4;
    style.left = Math.min(left, window.innerWidth - popoverW - 8);
    style.top  = Math.min(top,  window.innerHeight - 260);
  }

  return (
    <div ref={ref} className={styles.colorPopover} style={style} onClick={e => e.stopPropagation()}>
      <div className={styles.colorGrid}>
        {SWATCH_PRESETS.map(c => (
          <button
            key={c}
            className={`${styles.colorSwatch} ${c === currentColor ? styles.colorSwatchActive : ''}`}
            style={{ background: c }}
            onClick={(e) => { e.stopPropagation(); onSelect(c); onClose(); }}
          />
        ))}
      </div>
      <label className={styles.colorCustomRow}>
        <span className={styles.colorCustomLabel}>Custom</span>
        <input
          type="color"
          value={currentColor}
          className={styles.colorInput}
          onChange={(e) => { onSelect(e.target.value); onClose(); }}
          onClick={e => e.stopPropagation()}
        />
      </label>
    </div>
  );
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = (sec - m * 60).toFixed(3).padStart(6, '0');
  return `${m}:${s}`;
}

function fmtDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d)) return isoStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoStr;
  }
}

// ── Auto-select bar ───────────────────────────────────────────────────────────
function modeLabel(mode, sectors) {
  if (mode.type === 'best') {
    return mode.count === 1 ? 'Best 1' : `Best ${mode.count}`;
  }
  if (mode.type === 'sector_best') {
    const sec = sectors && sectors[mode.sectorIdx];
    const name = sec ? sec.label.replace('Sector ', 'S') : `S${mode.sectorIdx + 1}`;
    return mode.count === 1 ? `${name} Best` : `${name} ×${mode.count}`;
  }
  return 'Auto';
}

function AutoSelectBar({ mode, onChange, sectors }) {
  const [open,    setOpen]    = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const ref    = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: Math.max(4, r.right - 220) });
    }
    setOpen(v => !v);
  };

  const pick = (newMode) => { onChange(newMode); setOpen(false); };

  const isActive = (type, count, sectorIdx = undefined) =>
    mode.type === type && mode.count === count &&
    (sectorIdx === undefined || mode.sectorIdx === sectorIdx);

  return (
    <div ref={ref} className={styles.autoSelect}>
      <button
        ref={btnRef}
        className={styles.autoSelectBtn}
        onClick={handleOpen}
        title="Change auto-select mode"
      >
        {modeLabel(mode, sectors)} ▾
      </button>

      {open && (
        <div
          className={styles.autoSelectDropdown}
          style={{ top: dropPos.top, left: dropPos.left }}
        >
          <div className={styles.autoSelectGroup}>
            {[
              { count: 1, label: 'Best lap' },
              { count: 2, label: 'Best 2 laps' },
              { count: 3, label: 'Best 3 laps' },
              { count: 5, label: 'Best 5 laps' },
            ].map(({ count, label }) => (
              <button
                key={count}
                className={`${styles.autoSelectItem} ${isActive('best', count) ? styles.autoSelectItemActive : ''}`}
                onClick={() => pick({ type: 'best', count })}
              >
                {label}
              </button>
            ))}
          </div>

          {sectors && sectors.length > 0 && (
            <>
              <div className={styles.autoSelectSep} />
              {[
                { count: 1, label: 'Best sector lap' },
                { count: 3, label: '3 best sector laps' },
                { count: 5, label: '5 best sector laps' },
              ].map(({ count, label }) => (
                <div key={count} className={styles.autoSelectGroup}>
                  <div className={styles.autoSelectGroupLabel}>{label}</div>
                  <div className={styles.autoSelectSectorRow}>
                    {sectors.map((s, idx) => (
                      <button
                        key={s.id}
                        className={`${styles.autoSelectSector} ${isActive('sector_best', count, idx) ? styles.autoSelectSectorActive : ''}`}
                        onClick={() => pick({ type: 'sector_best', count, sectorIdx: idx })}
                        title={s.label}
                      >
                        {s.label.replace('Sector ', 'S')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Session picker modal ──────────────────────────────────────────────────────
const EMPTY_FACETS = {
  tracks: [], cars: [], drivers: [], temp_min: null, temp_max: null,
  date_min: null, date_max: null, has_wet: false, has_dry: false,
};
const PICKER_PAGE_SIZE = 50;
const PICKER_COLUMNS = [
  ...SESSION_TABLE_COLS.slice(0, 3),
  { key: null, label: 'Gap', col: 'colGap' },
  ...SESSION_TABLE_COLS.slice(3, -1),
];

function SessionPickerModal({
  onSelect, onClose, excludeSessionIds = [], trackName, anchorBestSeconds,
  currentUserId, isAdmin = false, adminModeActive = false, unitPrefs,
}) {
  const tempUnit = unitPrefs?.temp === 'C' ? 'C' : 'F';
  const storageTrack = (trackName || 'unknown').trim().toLowerCase();
  const restored = useMemo(
    () => readCompareSessionFilters(sessionStorage, currentUserId, storageTrack),
    [currentUserId, storageTrack]
  );
  const restoredTempRange = useMemo(() => {
    if (!restored.tempRange || !restored.tempUnit || restored.tempUnit === tempUnit) return restored.tempRange || null;
    return restored.tempRange.map(value => restored.tempUnit === 'F'
      ? (value - 32) * 5 / 9
      : value * 9 / 5 + 32);
  }, [restored.tempRange, restored.tempUnit, tempUnit]);

  const [sessions, setSessions] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [facetsReady, setFacetsReady] = useState(false);
  const [filterText, setFilterText] = useState(restored.filterText || '');
  const [filterCar, setFilterCar] = useState(restored.filterCar || '');
  const [filterDriver, setFilterDriver] = useState(restored.filterDriver || '');
  const [tempRange, setTempRange] = useState(restoredTempRange);
  const [dateField, setDateField] = useState(restored.dateField || 'session');
  const [conditions, setConditions] = useState(restored.conditions || 'all');
  const [activeTab, setActiveTab] = useState(restored.activeTab || 'all');
  const [sort, setSort] = useState(restored.sort || { col: 'date', dir: 'desc' });
  const [tableGrouped, setTableGrouped] = useState(restored.tableGrouped !== false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const fetchSeqRef = useRef(0);
  const fetchTimerRef = useRef(null);
  const sentinelRef = useRef(null);

  const cToDisplay = useCallback(value => tempUnit === 'F' ? value * 9 / 5 + 32 : value, [tempUnit]);
  const displayToC = useCallback(value => tempUnit === 'F' ? (value - 32) * 5 / 9 : value, [tempUnit]);
  const tempBounds = useMemo(() => {
    if (facets.temp_min == null || facets.temp_max == null) return null;
    const min = Math.floor(cToDisplay(facets.temp_min));
    const max = Math.ceil(cToDisplay(facets.temp_max));
    return max > min ? { min, max } : null;
  }, [facets.temp_min, facets.temp_max, cToDisplay]);
  const dateBounds = useMemo(() => {
    if (!facets.date_min || !facets.date_max) return null;
    const minValue = new Date(facets.date_min).getTime();
    const maxValue = new Date(facets.date_max).getTime();
    if (Number.isNaN(minValue) || Number.isNaN(maxValue)) return null;
    const min = Math.floor(minValue / DAY_MS) * DAY_MS;
    const max = Math.ceil(maxValue / DAY_MS) * DAY_MS;
    return max > min ? { min, max } : null;
  }, [facets.date_min, facets.date_max]);
  const conditionAvailability = useMemo(
    () => ({ wet: facets.has_wet, dry: facets.has_dry }),
    [facets.has_wet, facets.has_dry]
  );
  const {
    range: dateRange, setRange: setDateRange, applyPreset: applyDatePreset,
    reset: resetDateRange, sliderMin: dateSliderMin, sliderMax: dateSliderMax,
    activePreset: activeDatePreset,
  } = useDateRangePresets(dateBounds);
  const pendingDateRestoreRef = useRef(restored.dateRange
    ? { range: restored.dateRange, preset: restored.datePreset }
    : null);
  const [dateRestoreComplete, setDateRestoreComplete] = useState(!pendingDateRestoreRef.current);

  useEffect(() => {
    const onKeyDown = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => { resetDateRange(); }, [dateField]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dateBounds || !pendingDateRestoreRef.current) return;
    const saved = pendingDateRestoreRef.current;
    pendingDateRestoreRef.current = null;
    if (saved.preset) applyDatePreset(saved.preset);
    else setDateRange(saved.range);
    setDateRestoreComplete(true);
  }, [dateBounds, applyDatePreset, setDateRange]);

  const previousTempBoundsRef = useRef(null);
  useEffect(() => {
    const previous = previousTempBoundsRef.current;
    previousTempBoundsRef.current = tempBounds;
    if (!tempBounds) { setTempRange(value => value === null ? value : null); return; }
    setTempRange(value => {
      if (!value) return [tempBounds.min, tempBounds.max];
      const wasFull = previous && value[0] === previous.min && value[1] === previous.max;
      const next = wasFull
        ? [tempBounds.min, tempBounds.max]
        : [Math.max(tempBounds.min, Math.min(value[0], tempBounds.max)),
           Math.min(tempBounds.max, Math.max(value[1], tempBounds.min))];
      return next[0] === value[0] && next[1] === value[1] ? value : next;
    });
  }, [tempBounds]);

  useEffect(() => {
    if (facetsReady && filterCar && !facets.cars.includes(filterCar)) setFilterCar('');
  }, [facetsReady, facets.cars, filterCar]);
  useEffect(() => {
    if (facetsReady && filterDriver && !facets.drivers.includes(filterDriver)) setFilterDriver('');
  }, [facetsReady, facets.drivers, filterDriver]);
  useEffect(() => {
    if ((conditions === 'wet' && !conditionAvailability.wet) ||
        (conditions === 'dry' && !conditionAvailability.dry)) setConditions('all');
  }, [conditions, conditionAvailability]);

  const buildParams = useCallback(() => {
    const params = {
      tab: activeTab, sort: sort.col, sort_dir: sort.dir, page_size: PICKER_PAGE_SIZE,
    };
    if (trackName) params.track = trackName;
    const query = filterText.trim();
    if (query) params.q = query;
    if (filterCar) params.car = filterCar;
    if (filterDriver) params.driver = filterDriver;
    if (conditions !== 'all') params.condition = conditions;
    if (dateField !== 'session') params.date_field = dateField;
    if (tempBounds && tempRange &&
        (tempRange[0] > tempBounds.min || tempRange[1] < tempBounds.max)) {
      params.temp_min = displayToC(tempRange[0]);
      params.temp_max = displayToC(tempRange[1]);
    }
    if (dateBounds && dateRange &&
        (dateRange[0] > dateBounds.min || dateRange[1] < dateBounds.max)) {
      params.date_from = new Date(dateRange[0]).toISOString();
      params.date_to = new Date(dateRange[1]).toISOString();
    }
    return params;
  }, [activeTab, sort, trackName, filterText, filterCar, filterDriver, conditions,
      dateField, tempBounds, tempRange, displayToC, dateBounds, dateRange]);

  useEffect(() => {
    const sequence = ++fetchSeqRef.current;
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      const params = buildParams();
      setError('');
      listSessions(params)
        .then(data => {
          if (sequence !== fetchSeqRef.current) return;
          setSessions(data.sessions || []);
          setCursor(data.next_cursor || null);
          setHasMore(!!data.next_cursor);
        })
        .catch(() => {
          if (sequence !== fetchSeqRef.current) return;
          setSessions([]); setCursor(null); setHasMore(false);
          setError('Could not load sessions.');
        })
        .finally(() => { if (sequence === fetchSeqRef.current) setLoading(false); });
      getSessionFacets(params)
        .then(value => {
          if (sequence === fetchSeqRef.current) {
            setFacets(value);
            setFacetsReady(true);
          }
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(fetchTimerRef.current);
  }, [buildParams]);

  useEffect(() => {
    if (!facetsReady || !dateRestoreComplete) return;
    writeCompareSessionFilters(sessionStorage, currentUserId, storageTrack, {
      filterText, filterCar, filterDriver, tempRange, tempUnit,
      dateField, dateRange, datePreset: activeDatePreset,
      conditions, activeTab, sort, tableGrouped,
    });
  }, [facetsReady, dateRestoreComplete, currentUserId, storageTrack, filterText,
      filterCar, filterDriver, tempRange, tempUnit, dateField, dateRange,
      activeDatePreset, conditions, activeTab, sort, tableGrouped]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || !cursor) return;
    const sequence = fetchSeqRef.current;
    setLoadingMore(true);
    listSessions({ ...buildParams(), cursor })
      .then(data => {
        if (sequence !== fetchSeqRef.current) return;
        setSessions(current => [...current, ...(data.sessions || [])]);
        setCursor(data.next_cursor || null);
        setHasMore(!!data.next_cursor);
        setError('');
      })
      .catch(() => { if (sequence === fetchSeqRef.current) setError('Could not load more sessions.'); })
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, cursor, buildParams]);
  useEffect(() => {
    const element = sentinelRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '400px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [loadMore]);

  const excluded = useMemo(() => new Set(excludeSessionIds), [excludeSessionIds]);
  const visibleSessions = useMemo(() => sessions.filter(session =>
    isComparisonSessionVisible(session, excluded, isAdmin, adminModeActive)
  ), [sessions, excluded, isAdmin, adminModeActive]);
  const groupingActive = tableGrouped && sort.col === 'date';
  const groups = useMemo(
    () => groupingActive ? groupComparisonSessions(visibleSessions, sort.dir === 'desc') : [],
    [groupingActive, visibleSessions, sort.dir]
  );

  const renderSessionRow = session => {
    const disabled = !isComparisonSessionSelectable(session);
    const gap = comparisonGapSeconds(session, anchorBestSeconds);
    const selectSession = () => { if (!disabled) onSelect(session.session_id); };
    let weather = '—';
    if (session.weather) {
      const converted = convertWeatherUnits(
        session.weather,
        unitPrefs || { temp: 'F', speed: 'mph', pressure: 'psi' }
      );
      weather = `${sessionWeatherIcon(session.weather.condition)} ${converted.temp ?? '—'}`;
    }
    return (
      <tr
        key={session.session_id}
        className={disabled ? tableStyles.trDisabled : ''}
        onClick={selectSession}
        onKeyDown={event => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            selectSession();
          }
        }}
        tabIndex={disabled ? -1 : 0}
        title={disabled ? 'Original file not available — re-upload to restore' : 'Add this session'}
        aria-disabled={disabled || undefined}
        aria-label={`Add ${session.session_label || sessionTrackDisplayName(session) || 'session'} from ${session.date || 'unknown date'}`}
      >
        <td className={tableStyles.tdTrack} title={sessionTrackDisplayName(session) || 'Session'}>
          {sessionTrackDisplayName(session) || 'Session'}
          {sessionHasVideo(session) && <span className={tableStyles.tblVideoBadge}><Film size={12} /></span>}
          {session.shared_with_me && <span className={tableStyles.tblSharedBadge}>Shared</span>}
        </td>
        <td className={tableStyles.tdMono}
            title={session.date ? `${session.date}${session.time ? ` ${formatSessionTime12(session.time)}` : ''}` : undefined}>
          {session.date
            ? `${shortSessionYear(session.date)}${session.time ? ` ${formatSessionTime12(session.time)}` : ''}`
            : (formatSessionDateTimeCompact(session.created_at) || '—')}
        </td>
        <td className={tableStyles.tdLap}>{session.best_lap_time || '—'}</td>
        <td className={`${tableStyles.tdMono} ${styles.pickerGap} ${gap == null ? '' : gap <= 0 ? styles.pickerGapFaster : styles.pickerGapSlower}`}>
          {gap == null ? '—' : `${gap <= 0 ? '−' : '+'}${Math.abs(gap).toFixed(3)}`}
        </td>
        <td className={tableStyles.tdMono}>{session.lap_count ?? '—'}</td>
        <td className={tableStyles.tdMono}>{formatSessionDuration(session.duration)}</td>
        <td title={session.racer || undefined}>{session.racer || '—'}</td>
        <td title={session.vehicle || undefined}>{session.vehicle || '—'}</td>
        <td className={tableStyles.tdMono}>{sessionHasVideo(session) ? <Film size={12} /> : '—'}</td>
        <td>{weather}</td>
        <td className={tableStyles.tdMono} title={session.created_at || undefined}>
          {formatSessionDateTimeCompact(session.created_at) || '—'}
        </td>
      </tr>
    );
  };

  const tableRows = groupingActive
    ? groups.flatMap((group, index) => {
        if (group.sessions.length < 2) return group.sessions.map(renderSessionRow);
        const collapsed = collapsedGroups.has(group.key);
        const rail = index % 2 === 0 ? 'railA' : 'railB';
        return [
          <tr key={`header:${group.key}`} className={tableStyles.groupRow}>
            <td className={`${tableStyles.groupRowCell} ${tableStyles[rail]}`} colSpan={PICKER_COLUMNS.length}>
              <button
                className={styles.pickerGroupHeader}
                onClick={() => setCollapsedGroups(current => {
                  const next = new Set(current);
                  if (next.has(group.key)) next.delete(group.key); else next.add(group.key);
                  return next;
                })}
              >
                <span>{collapsed ? '▸' : '▾'} {trackName || 'Track Event'}</span>
                <span>{formatComparisonGroupDate(group)} · {group.sessions.length} sessions</span>
              </button>
            </td>
          </tr>,
          ...(collapsed ? [] : group.sessions.map(renderSessionRow)),
        ];
      })
    : visibleSessions.map(renderSessionRow);

  const filtersActive = !!(filterText || filterCar || filterDriver || conditions !== 'all' ||
    activeTab !== 'all' || (tempBounds && tempRange &&
      (tempRange[0] > tempBounds.min || tempRange[1] < tempBounds.max)) ||
    (dateBounds && dateRange && (dateRange[0] > dateBounds.min || dateRange[1] < dateBounds.max)));

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="session-picker-title"
           onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleGroup}>
            <span className={styles.modalTitle} id="session-picker-title">Select a Session</span>
            {trackName && <span className={styles.modalTrackBadge} title="Comparisons stay on the loaded track">🔒 {trackName}</span>}
          </div>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close session picker">×</button>
        </div>

        <div className={styles.modalControls}>
          <div className={styles.pickerPrimaryControls}>
            <SessionPrimaryFilters
              filterText={filterText} onFilterText={setFilterText} facets={facets}
              showTrack={false}
              filterCar={filterCar} onFilterCar={setFilterCar}
              filterDriver={filterDriver} onFilterDriver={setFilterDriver}
              autoFocus
            />
            <button
              className={`${tableStyles.groupToggleBtn} ${groupingActive ? tableStyles.groupToggleBtnActive : tableGrouped ? tableStyles.groupToggleBtnPaused : ''}`}
              onClick={() => setTableGrouped(value => !value)}
              title={tableGrouped && !groupingActive
                ? 'Grouping paused because the table is not sorted by Date'
                : 'Group sessions into events within three days'}
            >⁝⁝ Events</button>
          </div>

          <SessionAdvancedFilters
            tempBounds={tempBounds} tempRange={tempRange} onTempRange={setTempRange} tempUnit={tempUnit}
            conditionAvailability={conditionAvailability} conditions={conditions} onConditions={setConditions}
            dateBounds={dateBounds} dateRange={dateRange} onDateRange={setDateRange}
            dateField={dateField} onDateField={setDateField}
            dateSliderMin={dateSliderMin} dateSliderMax={dateSliderMax}
            activeDatePreset={activeDatePreset} onDatePreset={applyDatePreset}
            formatDate={fmtDate}
          />

          <SessionOwnershipTabs activeTab={activeTab} onActiveTab={setActiveTab} />
        </div>

        <div className={styles.modalBody}>
          {filtersActive && !loading && (
            <div className={tableStyles.filterCount}>{visibleSessions.length}{hasMore ? '+' : ''} sessions</div>
          )}
          {loading && <div className={styles.modalEmpty}>Loading sessions…</div>}
          {!loading && error && sessions.length === 0 && <div className={styles.modalEmpty}>{error}</div>}
          {!loading && !error && visibleSessions.length === 0 && !hasMore && (
            <div className={styles.modalEmpty}>
              {filtersActive ? 'No sessions match the active filters.' : `No other sessions at ${trackName || 'this track'}.`}
            </div>
          )}
          {!loading && (visibleSessions.length > 0 || hasMore) && (
            <div className={`${tableStyles.tableWrapper} ${styles.pickerTableWrapper}`}>
              <table className={`${tableStyles.sessionTable} ${styles.pickerTable}`}>
                <colgroup>
                  {PICKER_COLUMNS.map((column, index) => (
                    <col key={index} className={column.col === 'colGap' ? styles.colGap : column.col ? tableStyles[column.col] : undefined} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {PICKER_COLUMNS.map((column, index) => {
                      if (!column.key) return <th key={index}>{column.label}</th>;
                      const active = sort.col === column.key;
                      return (
                        <th key={index} className={`${tableStyles.thSortable} ${active ? tableStyles.thSorted : ''}`}
                            onClick={() => setSort(current => nextSessionSort(current, column.key))}
                            title="Click to sort">
                          {column.label}
                          {active && <span className={tableStyles.sortBadge}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>{tableRows}</tbody>
              </table>
            </div>
          )}
          <div ref={sentinelRef} className={tableStyles.scrollSentinel} aria-hidden="true" />
          {loadingMore && <div className={tableStyles.filterCount}>Loading more…</div>}
          {error && sessions.length > 0 && <div className={styles.pickerInlineError}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Driver section ────────────────────────────────────────────────────────────
// Put every cursor-linked panel on a finding's worst sample. Hover drives the
// trace + map cursors, click seeks the video / 3D panels (same pair the charts
// publish). The lap is selected first so the trace it lands on is loading.
function jumpToFinding(finding) {
  const w = finding?.worst;
  if (!w || w.lat == null || w.lon == null) return;
  const detail = {
    lat: w.lat, lon: w.lon,
    distFt: Number.isFinite(w.dist_m) ? w.dist_m * 3.28084 : null,
    videoTime: null,
  };
  window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail }));
  window.dispatchEvent(new CustomEvent('trackapp:chart-click', { detail }));
}


function DriverSection({
  title, accentColor,
  laps, selectedLaps, onToggle, loadingTraces,
  colors, customColors, onColorChange,
  carHealth = null,
}) {
  const [colorPicker, setColorPicker] = useState(null); // { lapIndex, anchorRect }
  const allLaps   = laps || [];
  const timedLaps = allLaps.filter(l => !l.is_outlap && !l.is_inlap);
  const bestTime  = getBestLapTime(allLaps);
  const timedLapDisplayNum = new Map(timedLaps.map((l, i) => [l.index, i + 1]));

  return (
    <>
      {/* Section header row */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionLabel} style={{ color: accentColor }}>{title}</span>
      </div>

      {/* Lap rows */}
      {allLaps.map((lap) => {
        const isOutIn   = lap.is_outlap || lap.is_inlap;
        const selected  = selectedLaps.includes(lap.index);
        const isLoading = loadingTraces?.has(lap.index);
        const delta     = isOutIn ? null : lap.lap_time_seconds - bestTime;
        const isBest    = isBestLap(lap, bestTime);
        const color     = getLapColor(lap.index, selectedLaps, colors, customColors);

        return (
          <div
            key={lap.index}
            className={`${styles.row} ${selected ? styles.selected : ''} ${isOutIn ? styles.outInRow : ''}`}
            onClick={() => onToggle(lap.index)}
            style={selected ? { borderLeft: `3px solid ${color}` } : {}}
          >
            {selected && (
              <div className={styles.colorSwatchWrap}>
                <button
                  className={styles.colorDot}
                  style={{ background: color }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (colorPicker?.lapIndex === lap.index) {
                      setColorPicker(null);
                    } else {
                      setColorPicker({ lapIndex: lap.index, anchorRect: e.currentTarget.getBoundingClientRect() });
                    }
                  }}
                  title="Change trace color"
                />
                {colorPicker?.lapIndex === lap.index && (
                  <ColorPickerPopover
                    currentColor={color}
                    anchorRect={colorPicker.anchorRect}
                    onSelect={(c) => onColorChange?.(lap.index, c)}
                    onClose={() => setColorPicker(null)}
                  />
                )}
              </div>
            )}
            <div className={styles.lapNum}>
              {isBest && <span className={styles.bestBadge}>▲</span>}
              {!isOutIn && carHealth?.lap_status?.[String(lap.lap_number)] && (
                <LapHealthDot
                  level={carHealth.lap_status[String(lap.lap_number)]}
                  findings={findingsForLap(carHealth.findings, lap.lap_number)}
                  onJump={(f) => { if (!selected) onToggle(lap.index); jumpToFinding(f); }}
                />
              )}
              {lap.is_outlap && <span className={styles.outInBadge}>OUT</span>}
              {lap.is_inlap  && <span className={styles.outInBadge}>IN</span>}
              L{timedLapDisplayNum.get(lap.index) ?? lap.lap_number}
            </div>
            <div className={styles.lapTime}>{lap.lap_time_str || formatTime(lap.lap_time_seconds)}</div>
            {!isBest && delta !== null && (
              <div className={styles.delta}>+{delta.toFixed(3)}s</div>
            )}
            {isLoading && <div className={styles.loadingDot} />}
          </div>
        );
      })}

    </>
  );
}

// ── S/F mismatch warning ─────────────────────────────────────────────────────
// Shown under a driver header when that session's laps were timed from a
// start/finish far from the reference (track canonical S/F, or Driver 1's when
// none is stored) — comparison traces/deltas won't line up until re-aligned.
function SfMismatchWarning({ offsetM, canRealign, onRealign, realigning }) {
  if (offsetM == null || offsetM < SF_MISMATCH_WARN_M) return null;
  return (
    <div
      className={styles.sfWarn}
      title={`This session's laps start ~${Math.round(offsetM)} m from the track start/finish (its logger used a different beacon). Distance-based comparison against other sessions is misaligned by that much. Re-align moves its lap boundaries onto the track start/finish — lap times change accordingly, since the timing line moves.`}
    >
      <span className={styles.sfWarnText}>⚠ S/F ~{Math.round(offsetM)} m off</span>
      {canRealign ? (
        <button
          className={styles.realignBtn}
          onClick={onRealign}
          disabled={realigning}
        >
          {realigning ? 'Aligning…' : 'Re-align'}
        </button>
      ) : (
        <span
          className={styles.sfWarnLock}
          title="Only the session owner (or an admin) can re-align it"
        >🔒</span>
      )}
    </div>
  );
}

// ── S/F re-aligned note ──────────────────────────────────────────────────────
// Muted indicator that a session's laps were re-segmented onto the track's
// start/finish, with an Undo that restores the logger's original boundaries.
function SfAlignedNote({ realigned, canUnalign, onUnalign, realigning }) {
  if (!realigned) return null;
  return (
    <div
      className={styles.sfAligned}
      title="This session's laps were re-segmented onto the track start/finish so it compares cleanly against other sessions. Undo restores the logger's original lap boundaries and times."
    >
      <span className={styles.sfAlignedText}>⚑ S/F re-aligned</span>
      {canUnalign && (
        <button
          className={styles.unalignBtn}
          onClick={onUnalign}
          disabled={realigning}
        >
          {realigning ? 'Restoring…' : 'Undo'}
        </button>
      )}
    </div>
  );
}

// ── LapSelector ───────────────────────────────────────────────────────────────
export default function LapSelector({
  // Session 1
  laps, selectedLaps, onToggle, loadingTraces,
  driverName1,
  customColors1, onColorChange1,
  carHealth1 = null,       // session.car_health (core/car_health.py) for the lap dots
  // Comparison drivers — one entry per loaded comparison session.
  // Each: { slot, laps, selectedLaps, onToggle, loadingTraces, customColors,
  //         onColorChange, palette, accent, name, onRemove }
  compareDrivers = [],
  onAddSession,            // (sessionId) => void — load into the next free slot
  onOpen3D = null,         // open the 3D ghost race on the ticked laps
  can3D = false,
  onCompare3D = null,      // 3D ghost race on each driver's best lap
  canCompare3D = false,
  canAddMore = false,      // false once the comparison cap is reached
  loadingAdd,              // truthy while a comparison session is loading
  excludeSessionIds = [],  // primary + all loaded comparison ids (hidden in picker)
  trackName,               // filter picker to same track (display + name fallback)
  currentUserId,
  isAdmin = false,
  adminModeActive = false,
  unitPrefs,
  // Auto-select mode
  lapSelectionMode, onLapSelectionModeChange, sectors,
  // Start/finish alignment (primary session; compare drivers carry their own
  // sfOffsetM/canRealign/onRealign/realigning in their descriptor)
  primarySfOffsetM = null, canRealignPrimary = false,
  onRealignPrimary, realigningPrimary = false,
  primarySfRealigned = false, canUnalignPrimary = false, onUnalignPrimary,
  // Collapse (desktop only)
  collapsed = false, onToggleCollapsed,
  // Full-width, touch-sized variant for the mobile lap drawer
  isMobile = false,
}) {
  const [showPicker, setShowPicker] = useState(false);

  if (!laps || laps.length === 0) return null;

  // Collapsed rail — a thin vertical bar that expands the panel when clicked.
  if (collapsed) {
    return (
      <div className={styles.rail} onClick={onToggleCollapsed} title="Expand laps">
        <button
          className={styles.railToggle}
          onClick={(e) => { e.stopPropagation(); onToggleCollapsed?.(); }}
          aria-label="Expand laps panel"
          title="Expand laps"
        >
          ›
        </button>
        <span className={styles.railLabel}>LAPS</span>
      </div>
    );
  }

  const handleSelect = (sessionId) => {
    setShowPicker(false);
    onAddSession?.(sessionId);
  };

  return (
    <>
      <div className={`${styles.panel} ${isMobile ? styles.mobile : ''}`}>
        <div className={styles.header}>
          <span>Laps</span>
          {lapSelectionMode && onLapSelectionModeChange && (
            <AutoSelectBar
              mode={lapSelectionMode}
              onChange={onLapSelectionModeChange}
              sectors={sectors}
            />
          )}
          {onToggleCollapsed && (
            <button
              className={styles.collapseBtn}
              onClick={onToggleCollapsed}
              aria-label="Collapse laps panel"
              title="Collapse laps"
            >
              ‹
            </button>
          )}
        </div>
        <div className={styles.list}>

          <SfMismatchWarning
            offsetM={primarySfOffsetM}
            canRealign={canRealignPrimary}
            onRealign={onRealignPrimary}
            realigning={realigningPrimary}
          />
          <SfAlignedNote
            realigned={primarySfRealigned}
            canUnalign={canUnalignPrimary}
            onUnalign={onUnalignPrimary}
            realigning={realigningPrimary}
          />
          <DriverSection
            title={driverName1 || 'Driver 1'}
            accentColor="#e8411a"
            laps={laps}
            selectedLaps={selectedLaps}
            onToggle={onToggle}
            loadingTraces={loadingTraces}
            colors={LAP_COLORS}
            customColors={customColors1}
            onColorChange={onColorChange1}
            carHealth={carHealth1}
          />

          {/* Scoped to the first session, which is why it sits with it. Racing
              every driver from here is the Compare in 3D button below the
              comparison list; keeping the two separate means each button's
              position matches what it acts on. */}
          {onOpen3D && (
            <div className={styles.uploadArea}>
              <button
                className={styles.uploadBtn}
                disabled={!can3D}
                onClick={() => can3D && onOpen3D()}
                title={can3D
                  ? "Race this session's selected laps against each other in 3D"
                  : 'Select at least one lap'}
              >
                View in 3D
              </button>
              <div className={styles.uploadHint}>This session — up to 4 laps, fastest first</div>
            </div>
          )}

          {compareDrivers.map((d) => (
            <div key={d.slot}>
              <div className={styles.divider} />

              {/* Comparison driver header */}
              <div className={styles.sectionHeader}>
                <span className={styles.sectionLabel} style={{ color: d.accent }}>
                  {d.name}
                </span>
                <div className={styles.sectionActions}>
                  {d.onRemove && (
                    <button
                      className={styles.removeBtn}
                      onClick={d.onRemove}
                      title="Remove comparison session"
                    >×</button>
                  )}
                </div>
              </div>

              <SfMismatchWarning
                offsetM={d.sfOffsetM}
                canRealign={d.canRealign}
                onRealign={d.onRealign}
                realigning={d.realigning}
              />
              <SfAlignedNote
                realigned={d.sfRealigned}
                canUnalign={d.canUnalign}
                onUnalign={d.onUnalign}
                realigning={d.realigning}
              />
              <DriverSection
                title={null}
                accentColor={d.accent}
                laps={d.laps}
                selectedLaps={d.selectedLaps}
                onToggle={d.onToggle}
                loadingTraces={d.loadingTraces}
                colors={d.palette}
                customColors={d.customColors}
                onColorChange={d.onColorChange}
                carHealth={d.carHealth}
              />
            </div>
          ))}

          {canAddMore && (
            <>
              <div className={styles.divider} />
              <div className={styles.uploadArea}>
                <button
                  className={`${styles.uploadBtn} ${loadingAdd ? styles.uploading : ''}`}
                  disabled={!!loadingAdd}
                  onClick={() => !loadingAdd && setShowPicker(true)}
                >
                  {loadingAdd ? 'Loading…' : '+ Add Session'}
                </button>
                <div className={styles.uploadHint}>Compare against another session</div>
              </div>
            </>
          )}

          {/* Same action as the 3D button up with the first session, offered
              again here because this is where you are once you've just added a
              comparison — and unlike up there, "compare" is literally true. Sits
              outside the canAddMore block so it survives a full four drivers,
              when the Add Session button above it is gone. */}
          {onCompare3D && compareDrivers.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.uploadArea}>
                <button
                  className={styles.uploadBtn}
                  disabled={!canCompare3D}
                  onClick={() => canCompare3D && onCompare3D()}
                  title={canCompare3D
                    ? 'Race the selected laps from every driver against each other in 3D'
                    : 'Select at least one lap on any driver'}
                >
                  Compare in 3D
                </button>
                <div className={styles.uploadHint}>All drivers — up to 4 laps, fastest first</div>
              </div>
            </>
          )}
        </div>
      </div>

      {showPicker && (
        <SessionPickerModal
          onSelect={handleSelect}
          onClose={() => setShowPicker(false)}
          excludeSessionIds={excludeSessionIds}
          trackName={trackName}
          anchorBestSeconds={getBestLapTime(laps)}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          adminModeActive={adminModeActive}
          unitPrefs={unitPrefs}
        />
      )}
    </>
  );
}
